import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://server.derashekub.com/api/v1';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== AUTH API ====================
export const authAPI = {
  login: (phone, password, confirm = false) =>
    api.post('/auth/login', { phone, password, confirm }),
  getMe: () => api.get('/auth/me'),
  changePassword: (oldPassword, newPassword) =>
    api.post('/auth/change-password', { old_password: oldPassword, new_password: newPassword }),
};

// ==================== MEMBERS API ====================
export const membersAPI = {
  // Management routes (staff access)
  register: (data) => api.post('/members/register', data),
  enroll: (data) => api.post('/members/enroll', data),
  
  // List routes
  getAll: (params) => api.get('/members/allMembers', { params }), // All members with filters
  getOnline: () => api.get('/members/online'), // Online drawers only
  getBatchMembers: (batchId, params) => api.get(`/members/batch/${batchId}`, { params }),
  
  // Member-specific routes
  getFinancialSummary: (id, batchId) => api.get(`/members/${id}/financial-summary`, { params: { batch_id: batchId } }),
  getPaymentStatus: (id) => api.get(`/members/${id}/status`),
  getDaysCovered: (id) => api.get(`/members/${id}/days-covered`),
  getWinnerStatus: (id) => api.get(`/members/${id}/winner-status`),
  getMemberDeposits: (id) => api.get(`/members/${id}/deposits`),
  getProfile: (id) => api.get(`/members/${id}/profile`),
  updateProfile: (id, data) => api.put(`/members/${id}/profile`, data),
  uploadProfilePicture: (id, formData) => api.post(`/members/${id}/profile-pic`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  removeProfilePicture: (id) => api.delete(`/members/${id}/profile-pic`),
  updateMember: (id, data) => api.put(`/members/${id}`, data),
  deleteMember: (id) => api.delete(`/members/${id}`),
};

// ==================== BATCHES API ====================
export const batchesAPI = {
  getAll: (params) => api.get('/batches', { params }),
  getById: (id) => api.get(`/batches/${id}`),
  getStatus: (id) => api.get(`/batches/${id}/status`),
  create: (data) => api.post('/batches', data),
  activate: (id) => api.patch(`/batches/${id}/activate`),
  close: (id) => api.patch(`/batches/${id}/close`),
  deleteBatch: (id) => api.delete(`/batches/${id}`),
  getPaymentStatus: (id) => api.get(`/batches/${id}/payment-status`),
  getLiveStats: (id) => api.get(`/batches/${id}/live-stats`),
};

// ==================== DRAWS API ====================
export const drawsAPI = {
  // List/query routes
  getAll: (params) => api.get('/draws', { params }),
  getLive: () => api.get('/draws/live'),
  getCandidates: (batchId) => api.get(`/draws/candidates/${batchId}`),
  getBatchDraws: (batchId) => api.get(`/draws/batch/${batchId}`),
  
  // Draw management
  start: (data) => api.post('/draws/start', data),
  startAll: () => api.post('/draws/start-all'),
  
  // Specific draw operations
  getById: (id) => api.get(`/draws/${id}`),
  exportWinners: (id) => api.get(`/draws/${id}/export-winners`),
  drawerResponse: (id, data) => api.post(`/draws/${id}/drawer-response`, data),
  complete: (id) => api.post(`/draws/${id}/complete`),
  cancel: (id) => api.post(`/draws/${id}/cancel`),
};

// ==================== DEPOSITS API ====================
export const depositsAPI = {
  initiateDeposit: (data) => api.post('/deposits/initiate', data),
  providerCallback: (provider, data) => api.post(`/deposits/callback/${provider}`, data),
  manualDeposit: (data) => api.post('/deposits/manual', data),
  getHistory: (params) => api.get('/deposits/history', { params }),
  getMyHistory: (params) => api.get('/deposits/my-history', { params }),
};

// ==================== WINNERS API ====================
export const winnersAPI = {
  getAll: (params) => api.get('/winners', { params }),
  getById: (id) => api.get(`/winners/${id}`),
  markPaid: (id, data) => api.patch(`/winners/${id}/mark-paid`, data),
};

// ==================== TICKETS API ====================
export const ticketsAPI = {
  getMyTickets: (params) => api.get('/tickets/mine', { params }),
  getTicketHistory: (params) => api.get('/tickets/history', { params }),
  getTicket: (id) => api.get(`/tickets/${id}`),
};

// ==================== NOTIFICATIONS API ====================
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications/all', { params }),
  create: (data) => api.post('/notifications', data),
  markAllRead: () => api.post('/notifications/mark-all-read'),
  markRead: (id) => api.patch(`/notifications/${id}`),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// ==================== REMINDERS API ====================
export const remindersAPI = {
  getAll: (params) => api.get('/reminders', { params }),
  markUsed: (id) => api.patch(`/reminders/${id}/use`),
  autoAllocate: () => api.post('/reminders/auto-allocate'),
};

// ==================== STAFF API ====================
export const staffAPI = {
  getAll: () => api.get('/staff'),
  getAuditLogs: (params) => api.get('/staff/audit-logs', { params }),
  getById: (id) => api.get(`/staff/${id}`),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.patch(`/staff/${id}`, data),
  delete: (id) => api.delete(`/staff/${id}`),
};

// ==================== REPORTS API ====================
export const reportsAPI = {
  getSummary: () => api.get('/reports/summary'),
  getBatchReport: (id) => api.get(`/reports/batch/${id}`),
  getMemberReport: (id) => api.get(`/reports/members/${id}`),
  getRemindersReport: () => api.get('/reports/reminders'),
  getWinnersReport: () => api.get('/reports/winners'),
  getFeesReport: () => api.get('/reports/fees'),
};

// ==================== SETTINGS API ====================
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.patch('/settings', data),
  refresh: () => api.post('/settings/refresh'),
  getBatch: (batchId) => api.get(`/settings/batch/${batchId}`),
  updateBatch: (batchId, data) => api.patch(`/settings/batch/${batchId}`, data),
};

// ==================== SYSTEM API ====================
export const systemAPI = {
  healthCheck: () => api.get('/system/health'),
  getStats: () => api.get('/system/stats'),
  getLogs: (params) => api.get('/system/logs', { params }),
  reload: () => api.post('/system/reload'),
};

export default api;
